import * as React from 'react'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'
import { ContractTaxonomyFragment } from 'generated/resources/graphql'
import type moment from 'moment'

import i18n from 'modules/core/lib/i18n'

import type SupplementForm from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/SupplementForm'
import type SupplementsFormStore from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/store'

import FormLines from './FormLines'

interface Props {
  forms: SupplementForm[]
  store: SupplementsFormStore
  taxonomies: ContractTaxonomyFragment[]
  itemToHighlight?: { supplementId: number } | null
  readonly: boolean
  date: moment.Moment
  frequency: string
  hover: boolean
  toggleTaxonomyCreationModal: (value: boolean) => void
}

interface FormDisplayProps {
  props: Props
  supplementForms: SupplementForm[]
  sourceText: string
  icon: React.ComponentProps<typeof Icon.Medium>['icon']
  hover: boolean
}

const SupplementFormsDisplay = ({
  props,
  supplementForms,
  sourceText,
  icon,
  hover,
}: FormDisplayProps) => {
  return (
    <Box
      paddingY="s16"
      paddingX="s24"
      background="grey100"
      border={{
        all: {
          width: 's1',
          style: 'solid',
          color: 'grey300',
        },
      }}
      borderRadius={{ all: 'abs012' }}
    >
      <Box flexDirection="row" gap="s8" paddingBottom="s12">
        <Icon.Medium icon={icon} color="grey600" />
        <Text.Normal weight="medium">{sourceText}</Text.Normal>
        <Text.Normal weight="medium">({supplementForms.length})</Text.Normal>
      </Box>
      <FormLines {...{ ...props, forms: supplementForms, hover }} />
    </Box>
  )
}

export default function FormGroups(props: Props) {
  const { forms, taxonomies, hover } = props

  const supplementsByContractForms: SupplementForm[] = []
  const importedSupplements: SupplementForm[] = []
  const supplementsReimbursedFromSpending: SupplementForm[] = []
  const supplementsReimbursedFromTimetracking: SupplementForm[] = []
  const supplementsFromBenefits: SupplementForm[] = []
  const manualSupplements: SupplementForm[] = []

  forms.forEach((form) => {
    if (form.data().contracts_compensation_id) {
      supplementsByContractForms.push(form)
    } else if (form.data().supplement_bulk_import_id) {
      importedSupplements.push(form)
    } else if (form.data().source_id || form.data().source_type) {
      const source_type: string = form.data().source_type
      if (source_type.includes('Expenses')) {
        supplementsReimbursedFromSpending.push(form)
      } else if (source_type.includes('Benefits')) {
        supplementsFromBenefits.push(form)
      } else {
        supplementsReimbursedFromTimetracking.push(form)
      }
    } else {
      manualSupplements.push(form)
    }
  })

  const compareSupplementsByContractForms = (s1: SupplementForm, s2: SupplementForm) => {
    let result = s1.compensationType.localeCompare(s2.compensationType)

    if (result === 0) {
      const taxonomy1 =
        taxonomies.find((t) => t.id == s1.get('contracts_taxonomy_id').value)?.name ?? ''
      const taxonomy2 =
        taxonomies.find((t) => t.id == s2.get('contracts_taxonomy_id').value)?.name ?? ''
      result = taxonomy1.localeCompare(taxonomy2)
    }

    if (result === 0) {
      result = s1.get('contracts_compensation_id').value - s2.get('contracts_compensation_id').value
    }

    return result
  }

  supplementsByContractForms.sort(compareSupplementsByContractForms)

  return (
    <Box gap="s24" paddingBottom="s24">
      {manualSupplements.length > 0 && (
        <SupplementFormsDisplay
          props={props}
          supplementForms={manualSupplements}
          sourceText={i18n.t('handoffs.supplements.created_manually')}
          icon="pencil"
          hover={hover}
        />
      )}
      {importedSupplements.length > 0 && (
        <SupplementFormsDisplay
          props={props}
          supplementForms={importedSupplements}
          sourceText={i18n.t('handoffs.supplements.imported')}
          icon="upload"
          hover={hover}
        />
      )}
      {supplementsByContractForms.length > 0 && (
        <SupplementFormsDisplay
          props={props}
          supplementForms={supplementsByContractForms}
          sourceText={i18n.t('handoffs.supplements.from_contract')}
          icon="file-filled"
          hover={hover}
        />
      )}
      {supplementsReimbursedFromTimetracking.length > 0 && (
        <SupplementFormsDisplay
          props={props}
          supplementForms={supplementsReimbursedFromTimetracking}
          sourceText={i18n.t('handoffs.supplements.from_timetracking')}
          icon="timer"
          hover={hover}
        />
      )}
      {supplementsReimbursedFromSpending.length > 0 && (
        <SupplementFormsDisplay
          props={props}
          supplementForms={supplementsReimbursedFromSpending}
          sourceText={i18n.t('handoffs.supplements.from_spending')}
          icon="expenses"
          hover={hover}
        />
      )}
      {supplementsFromBenefits.length > 0 && (
        <SupplementFormsDisplay
          props={props}
          supplementForms={supplementsFromBenefits}
          sourceText={i18n.t('handoffs.supplements.from_benefits')}
          icon="benefits"
          hover={hover}
        />
      )}
    </Box>
  )
}
